import ProtoBufService from './ProtoBufService'
import * as CameraSettingsPB from './libs/messaging/camera_settings_pb'
import { assignPayloadByDict } from './utils'

export namespace CameraSettings {
  export enum LowLightFilterMode {
    OFF = 0,
    ON = 1,
    AUTO = 2,
  }

  export enum LowLightFilterProfile {
    DEFAULT = 0,
    LOW = 1,
    MEDIUM = 2,
    HIGH = 3,
  }

  export enum AccessPriorityMode {
    ALWAYS_ACCEPT = 0,
    OWNER_FIRST = 1,
    ALWAYS_REJECT = 2,
  }

  export enum DetectionMode {
    DISABLED = 0,
    MOTION = 1,
    PERSON = 2,
  }

  export enum Sensitivity {
    DEFAULT = 0,
    LOW = 1,
    MEDIUM = 2,
    HIGH = 3,
  }

  export enum Shape {
    POLYGON = 0,
    RECTANGLE = 1,
  }

  export type CameraSettings = CameraSettingsPB.CameraSettingsResponse.AsObject | undefined
  
  export class CameraSettingsService extends ProtoBufService {

    constructor() {
      super()
      this.service = ProtoBufService.ServiceType.CAMERA_SETTINGS
    }

    setLens(id: number, lensId: number) {
      const payload = new CameraSettingsPB.CameraLensSetting()
      payload.setLensId(lensId)
      return this.request(id, CameraSettingsPB.CameraSettingControls.LENS, payload)
    }

    setEnabled(id: number, enabled: boolean) {
      const payload = new CameraSettingsPB.CameraEnabledSetting()
      payload.setEnabled(enabled)
      return this.request(id, CameraSettingsPB.CameraSettingControls.ENABLE, payload)
    }

    setAlias(id: number, alias: string) {
      const payload = new CameraSettingsPB.CameraAliasSetting()
      payload.setAlias(alias)
      return this.request(id, CameraSettingsPB.CameraSettingControls.ALIAS, payload)
    }

    setLowLightFilter(id: number, mode: LowLightFilterMode, profile: LowLightFilterProfile) {
      const payload = new CameraSettingsPB.LowLightFilterSetting()
      switch (mode) {
        case LowLightFilterMode.OFF:
          payload.setMode(CameraSettingsPB.LowLightFilterSetting.LowLightFilterMode.OFF)
          break
        case LowLightFilterMode.ON:
          payload.setMode(CameraSettingsPB.LowLightFilterSetting.LowLightFilterMode.ON)
          break
        case LowLightFilterMode.AUTO:
          payload.setMode(CameraSettingsPB.LowLightFilterSetting.LowLightFilterMode.AUTO)
          break
        default:
          payload.setMode(CameraSettingsPB.LowLightFilterSetting.LowLightFilterMode.OFF)
          break
      }
      switch (profile) {
        case LowLightFilterProfile.DEFAULT:
          payload.setProfile(CameraSettingsPB.LowLightFilterSetting.LowLightFilterProfile.DEFAULT)
          break
        case LowLightFilterProfile.LOW:
          payload.setProfile(CameraSettingsPB.LowLightFilterSetting.LowLightFilterProfile.LOW)
          break
        case LowLightFilterProfile.MEDIUM:
          payload.setProfile(CameraSettingsPB.LowLightFilterSetting.LowLightFilterProfile.MEDIUM)
          break
        case LowLightFilterProfile.HIGH:
          payload.setProfile(CameraSettingsPB.LowLightFilterSetting.LowLightFilterProfile.HIGH)
          break
        default:
          break
      }
      return this.request(id, CameraSettingsPB.CameraSettingControls.LOWLIGHT_FILTER, payload)
    }

    setAccessPriority(id: number, mode: AccessPriorityMode) {
      const payload = new CameraSettingsPB.AccessPrioritySetting()
      switch (mode) {
        case 0:
          payload.setMode(CameraSettingsPB.AccessPrioritySetting.AccessPriorityMode.ALWAYS_ACCEPT)
          break
        case 1:
          payload.setMode(CameraSettingsPB.AccessPrioritySetting.AccessPriorityMode.OWNER_FIRST)
          break
        case 2:
          payload.setMode(CameraSettingsPB.AccessPrioritySetting.AccessPriorityMode.ALWAYS_REJECT)
          break
        default:
          break
      }
      return this.request(id, CameraSettingsPB.CameraSettingControls.ACCESS_PRIORITY, payload)
    }

    setDetectionMode(id: number, enabled: boolean, mode: DetectionMode, sensitivity: Sensitivity) {
      const payload = new CameraSettingsPB.DetectionModeSetting()
      payload.setEnabled(enabled)
      switch (mode) {
        case 0:
          payload.setMode(CameraSettingsPB.DetectionModeSetting.DetectionMode.DISABLED)
          break
        case 1:
          payload.setMode(CameraSettingsPB.DetectionModeSetting.DetectionMode.MOTION)
          break
        case 2:
          payload.setMode(CameraSettingsPB.DetectionModeSetting.DetectionMode.PERSON)
          break
        default:
          break
      }
      switch (sensitivity) {
        case 0:
          payload.setSensitivity(CameraSettingsPB.DetectionModeSetting.Sensitivity.DEFAULT)
          break
        case 1:
          payload.setSensitivity(CameraSettingsPB.DetectionModeSetting.Sensitivity.LOW)
          break
        case 2:
          payload.setSensitivity(CameraSettingsPB.DetectionModeSetting.Sensitivity.MEDIUM)
          break
        case 3:
          payload.setSensitivity(CameraSettingsPB.DetectionModeSetting.Sensitivity.HIGH)
          break
        default:
          break
      }
      return this.request(id, CameraSettingsPB.CameraSettingControls.DETECTION_MODE, payload)
    }

    setDetectionReminder(id: number, enabled: boolean) {
      const payload = new CameraSettingsPB.DetectionReminderSetting()
      payload.setEnabled(enabled)
      return this.request(id, CameraSettingsPB.CameraSettingControls.DETECTION_REMINDER, payload)
    }

    setDetectionSchedule(id: number, enabled: boolean, schedule: string) {
      const payload = new CameraSettingsPB.DetectionScheduleSetting()
      payload.setEnabled(enabled)
      payload.setSchedule(schedule)
      return this.request(id, CameraSettingsPB.CameraSettingControls.DETECTION_SCHEDULE, payload)
    }

    setDetectionZone(id: number, enabled: boolean, shape: Shape, zones: Array<{ coordinates: { x: number, y: number }[] }>) {
      const payload = new CameraSettingsPB.DetectionZoneSetting()
      payload.setEnabled(enabled)
      const shapeType = new CameraSettingsPB.DetectionZoneSetting.SupportedShape()
      switch (shape) {
        case Shape.POLYGON:
          shapeType.setType(CameraSettingsPB.DetectionZoneSetting.SupportedShape.Shape.POLYGON)
          break
        case Shape.RECTANGLE:
          shapeType.setType(CameraSettingsPB.DetectionZoneSetting.SupportedShape.Shape.RECTANGLE)
          break
        default:
          break
      }
      payload.setShapeType(shapeType)
      if (Array.isArray(zones)) {
        const zoneList = zones.map(zone => {
          const config = new CameraSettingsPB.DetectionZoneSetting.ZoneConfig()
          const coordinate = zone.coordinates.map(({x, y}) => {
            const data = new CameraSettingsPB.DetectionZoneSetting.ZoneConfig.Coordinate()
            data.setX(x)
            data.setY(y)
            return data;
          })
          config.setCoordinateList(coordinate)
          return config;
        })
        payload.setZonesList(zoneList)
      }
      return this.request(id, CameraSettingsPB.CameraSettingControls.DETECTION_ZONE, payload)
    }

    setZoomInLock(id: number, enabled: boolean) {
      const payload = new CameraSettingsPB.ZoomInLockSetting()
      payload.setEnabled(enabled)
      return this.request(id, CameraSettingsPB.CameraSettingControls.ZOOM_IN_LOCK, payload)
    }

    setContinuousRecording(id: number, enabled: boolean) {
      const payload = new CameraSettingsPB.ContinuousRecordingSetting()
      payload.setEnabled(enabled)
      return this.request(id, CameraSettingsPB.CameraSettingControls.CONTINUOUS_RECORDING, payload)
    }

    setAutoPowerSaving(id: number, enabled: boolean) {
      const payload = new CameraSettingsPB.AutoPowerSavingSetting()
      payload.setEnabled(enabled)
      return this.request(id, CameraSettingsPB.CameraSettingControls.AUTO_POWER_SAVING, payload)
    }

    setStatusLed(id: number, enabled: boolean) {
      const payload = new CameraSettingsPB.StatusLedSetting()
      payload.setEnabled(enabled)
      return this.request(id, CameraSettingsPB.CameraSettingControls.STATUS_LED, payload)
    }

    setRotation(id: number, degree: number) {
      const payload = new CameraSettingsPB.RotationSetting()
      payload.setDegree(degree)
      return this.request(id, CameraSettingsPB.CameraSettingControls.ROTATION, payload)
    }
    
    setMicrophone(id: number, enabled: boolean) {
      const payload = new CameraSettingsPB.CameraMicrophoneSetting()
      payload.setEnabled(enabled)
      return this.request(id, CameraSettingsPB.CameraSettingControls.MICROPHONE, payload)
    }

    getCameraSettings(id: number) {
      const payload = new CameraSettingsPB.CameraSettingsRequest()
      return this.request(id, CameraSettingsPB.CameraSettingControls.ALL_SETTINGS, payload)
    }

    respondCameraSettings(id: number, localSetting: CameraSettingsPB.CameraSettingsResponse.AsObject) {
      if (localSetting === null) {
        throw Error("Viewer side camera setting service dont suppose to send out CameraSettingRespond!")
      }
      const payload = new CameraSettingsPB.CameraSettingsResponse()
      let objs = {
        settingsRevision: localSetting.settingsRevision,
        cameraLensSetting: new CameraSettingsPB.CameraLensSetting(),
        cameraEnabledSetting: new CameraSettingsPB.CameraEnabledSetting(),
        cameraAliasSetting: new CameraSettingsPB.CameraAliasSetting(),
        lowlightFilterSetting: new CameraSettingsPB.LowLightFilterSetting(),
        accessPrioritySetting: new CameraSettingsPB.AccessPrioritySetting(),
        detectionModeSetting: new CameraSettingsPB.DetectionModeSetting(),
        continuousRecordingSetting: new CameraSettingsPB.ContinuousRecordingSetting(),
        autoPowerSavingSetting: new CameraSettingsPB.AutoPowerSavingSetting(),
        cameraMicrophoneSetting: new CameraSettingsPB.CameraMicrophoneSetting(),
        rotationSetting: new CameraSettingsPB.RotationSetting(),
        detectionReminderSetting: new CameraSettingsPB.DetectionReminderSetting(),
        detectionScheduleSetting: new CameraSettingsPB.DetectionScheduleSetting(),
        // detectionZoneSetting: new CameraSettingsPB.DetectionZoneSetting(),
        zoomInLockSetting: new CameraSettingsPB.ZoomInLockSetting(),
        statusLedSetting: new CameraSettingsPB.StatusLedSetting(),
      }
      
      for (const key in objs) {
        // @ts-ignore: TS7053
        assignPayloadByDict(objs[key], localSetting[key])
      }
      assignPayloadByDict(payload, objs)
      return this.response(id, CameraSettingsPB.CameraSettingControls.ALL_SETTINGS, payload)
    }
  
    onRequest({ control, payload }: { control: number, payload: Uint8Array }) {
      let data;
      switch (control) {
        case CameraSettingsPB.CameraSettingControls.ALL_SETTINGS:
          return {
            type: 'getCameraSettings',
            data: null,
          }
        case CameraSettingsPB.CameraSettingControls.LENS:
          data = CameraSettingsPB.CameraLensSetting.deserializeBinary(payload)
          return {
            type: 'setLens',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.ENABLE:
          data = CameraSettingsPB.CameraEnabledSetting.deserializeBinary(payload)
          return {
            type: 'setEnabled',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.ALIAS:
          data = CameraSettingsPB.CameraAliasSetting.deserializeBinary(payload)
          return {
            type: 'setAlias',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.LOWLIGHT_FILTER:
          data = CameraSettingsPB.LowLightFilterSetting.deserializeBinary(payload)
          return {
            type: 'setLowLightFilter',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.ACCESS_PRIORITY:
          data = CameraSettingsPB.AccessPrioritySetting.deserializeBinary(payload)
          return {
            type: 'setAccessPriority',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.DETECTION_MODE:
          data = CameraSettingsPB.DetectionModeSetting.deserializeBinary(payload)
          return {
            type: 'setDetectionMode',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.DETECTION_REMINDER:
          data = CameraSettingsPB.DetectionReminderSetting.deserializeBinary(payload)
          return {
            type: 'setDetectionReminder',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.DETECTION_SCHEDULE:
          data = CameraSettingsPB.DetectionScheduleSetting.deserializeBinary(payload)
          return {
            type: 'setDetectionSchedule',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.DETECTION_ZONE:
          data = CameraSettingsPB.DetectionZoneSetting.deserializeBinary(payload)
          return {
            type: 'setDetectionZone',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.ZOOM_IN_LOCK:
          data = CameraSettingsPB.ZoomInLockSetting.deserializeBinary(payload)
          return {
            type: 'setZoomInLock',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.CONTINUOUS_RECORDING:
          data = CameraSettingsPB.ContinuousRecordingSetting.deserializeBinary(payload)
          return {
            type: 'setContinuousRecording',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.AUTO_POWER_SAVING:
          data = CameraSettingsPB.AutoPowerSavingSetting.deserializeBinary(payload)
          return {
            type: 'setPowerSaving',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.STATUS_LED:
          data = CameraSettingsPB.StatusLedSetting.deserializeBinary(payload)
          return {
            type: 'setStatusLed',
            data: data.toObject()
          }
        // ! OSDSetting is removed(comment out) from lastest version of schema
        // case CameraSettingsPB.CameraSettingControls.OSD:
        //   data = CameraSettingsPB.CameraLensSetting.deserializeBinary(payload)
        //   return {
        //     type: 'setOSD',
        //     data: data.toObject()
        //   }
        case CameraSettingsPB.CameraSettingControls.ROTATION:
          data = CameraSettingsPB.RotationSetting.deserializeBinary(payload)
          return {
            type: 'setRotation',
            data: data.toObject()
          }
        case CameraSettingsPB.CameraSettingControls.MICROPHONE:
          data = CameraSettingsPB.CameraMicrophoneSetting.deserializeBinary(payload)
          return {
            type: 'setMicrophone',
            data: data.toObject()
          }
        default:
          break
      }
      return null
    }

    onResponse({ control, payload }: { control: number, payload: Uint8Array }) {
      let data;
      switch (control) {
        case CameraSettingsPB.CameraSettingControls.ALL_SETTINGS:
          data = CameraSettingsPB.CameraSettingsResponse.deserializeBinary(payload)
          return {
            type: 'cameraSettings',
            data: data.toObject() as CameraSettings
          }
        case CameraSettingsPB.CameraSettingControls.LENS:
          return {
            type: 'cameraLens',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.ENABLE:
          return {
            type: 'cameraEnabled',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.ALIAS:
          return {
            type: 'cameraAlias',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.LOWLIGHT_FILTER:
          return {
            type: 'cameraLowLightFilter',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.ACCESS_PRIORITY:
          return {
            type: 'cameraAccessPriority',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.DETECTION_MODE:
          return {
            type: 'cameraDetectionMode',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.DETECTION_REMINDER:
          return {
            type: 'cameraDetectionReminder',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.DETECTION_SCHEDULE:
          return {
            type: 'cameraDetectionSchedule',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.DETECTION_ZONE:
          return {
            type: 'cameraDetectionZone',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.ZOOM_IN_LOCK:
          return {
            type: 'cameraZoomInLock',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.CONTINUOUS_RECORDING:
          return {
            type: 'cameraContinuousRecording',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.AUTO_POWER_SAVING:
          return {
            type: 'cameraPowerSaving',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.STATUS_LED:
          return {
            type: 'cameraStatusLed',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.OSD:
          return {
            type: 'cameraOSD',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.ROTATION:
          return {
            type: 'cameraRotation',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        case CameraSettingsPB.CameraSettingControls.MICROPHONE:
          return {
            type: 'cameraMicrophone',
            data: ProtoBufService.prototype.onResponse.call(this, { control, payload })
          }
        default:
          break
      }
      return null
    }
  }
}

export default CameraSettings.CameraSettingsService;